<template>
  <div class="user-feedback">
    <!-- 悬浮按钮 -->
    <button 
      v-if="!isOpen" 
      @click="openFeedback" 
      class="feedback-trigger"
      :title="$t('feedback.openFeedback')"
    >
      💬
    </button>

    <!-- 反馈表单弹窗 -->
    <div v-if="isOpen" class="feedback-modal" @click.self="closeFeedback">
      <div class="feedback-content">
        <div class="feedback-header">
          <h3>{{ $t('feedback.title') }}</h3>
          <button @click="closeFeedback" class="close-btn" :title="$t('feedback.close')">×</button>
        </div>
        
        <form @submit.prevent="submitFeedback" class="feedback-form">
          <!-- 姓名输入 -->
          <div class="form-group">
            <label for="userName">{{ $t('feedback.name') }}</label>
            <input 
              id="userName"
              v-model="formData.name" 
              type="text" 
              :placeholder="$t('feedback.namePlaceholder')"
              required
              class="form-input"
            />
          </div>

          <!-- 评分 -->
          <div class="form-group">
            <label>{{ $t('feedback.rating') }}</label>
            <div class="rating-container">
              <div class="stars">
                <button 
                  v-for="star in 5" 
                  :key="star"
                  type="button"
                  @click="setRating(star)"
                  class="star-btn"
                  :class="{ active: star <= formData.rating }"
                >
                  ⭐
                </button>
              </div>
              <span class="rating-text">{{ $t('feedback.ratingText', { rating: formData.rating }) }}</span>
            </div>
          </div>

          <!-- 反馈内容 -->
          <div class="form-group">
            <label for="feedbackMessage">{{ $t('feedback.message') }}</label>
            <textarea 
              id="feedbackMessage"
              v-model="formData.message" 
              :placeholder="$t('feedback.messagePlaceholder')"
              rows="4"
              required
              class="form-textarea"
            ></textarea>
          </div>

          <!-- 验证码 -->
          <div class="form-group">
            <label>{{ $t('feedback.captcha') }}</label>
            <SimpleCaptcha ref="captchaRef" />
          </div>

          <!-- 提交按钮 -->
          <div class="form-actions">
            <button type="button" @click="closeFeedback" class="btn-cancel">
              {{ $t('feedback.cancel') }}
            </button>
            <button 
              type="submit" 
              :disabled="isSubmitting || !isFormValid"
              class="btn-submit"
            >
              <span v-if="isSubmitting">{{ $t('feedback.submitting') }}</span>
              <span v-else>{{ $t('feedback.submit') }}</span>
            </button>
          </div>
        </form>

        <!-- 提交结果提示 -->
        <div v-if="submitResult" class="submit-result" :class="submitResult.type">
          <p>{{ submitResult.message }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import emailjs from '@emailjs/browser'
import { EMAILJS_CONFIG } from '../config/emailjs'
import { antiSpamManager } from '../utils/antiSpam'
import { analytics } from '../utils/analytics'
import SimpleCaptcha from './SimpleCaptcha.vue'

const { t } = useI18n()

// 表单数据
const formData = ref({
  name: '',
  rating: 0,
  message: ''
})

// 状态管理
const isOpen = ref(false)
const isSubmitting = ref(false)
const submitResult = ref<{ type: 'success' | 'error', message: string } | null>(null)
const captchaRef = ref<InstanceType<typeof SimpleCaptcha> | null>(null)

// EmailJS 配置
const EMAILJS_SERVICE_ID = EMAILJS_CONFIG.SERVICE_ID
const EMAILJS_TEMPLATE_ID = EMAILJS_CONFIG.TEMPLATE_ID
const EMAILJS_PUBLIC_KEY = EMAILJS_CONFIG.PUBLIC_KEY

// 表单验证
const isFormValid = computed(() => {
  return formData.value.name.trim() && 
         formData.value.rating > 0 && 
         formData.value.message.trim() &&
         captchaRef.value?.isValid
})

// 打开反馈表单
function openFeedback() {
  isOpen.value = true
  submitResult.value = null
  // 跟踪反馈表单打开事件
  analytics.trackFeedbackOpen()
}

// 关闭反馈表单
function closeFeedback() {
  isOpen.value = false
  // 延迟重置表单，避免关闭动画时看到重置
  setTimeout(() => {
    resetForm()
  }, 300)
}

// 设置评分
function setRating(rating: number) {
  formData.value.rating = rating
}

// 重置表单
function resetForm() {
  formData.value = {
    name: '',
    rating: 0,
    message: ''
  }
  submitResult.value = null
  // 重置验证码
  if (captchaRef.value) {
    captchaRef.value.reset()
  }
}

// 提交反馈
async function submitFeedback() {
  if (!isFormValid.value) return

  // 跟踪反馈提交事件
  analytics.trackFeedbackSubmit(formData.value.rating, formData.value.message.length)

  // 检查防刷限制
  const spamCheck = antiSpamManager.canSend()
  if (!spamCheck.allowed) {
    submitResult.value = {
      type: 'error',
      message: spamCheck.reason || t('feedback.submitError')
    }
    // 跟踪防刷限制事件
    analytics.trackFeedbackError('spam_protection')
    return
  }

  isSubmitting.value = true
  submitResult.value = null

  try {
    // 准备模板参数
    const templateParams = {
      from_name: formData.value.name,
      rating: formData.value.rating,
      message: formData.value.message
    }

    // 发送邮件
    await emailjs.send(
      EMAILJS_SERVICE_ID,
      EMAILJS_TEMPLATE_ID,
      templateParams,
      EMAILJS_PUBLIC_KEY
    )

    // 记录发送成功
    const userId = antiSpamManager.generateUserFingerprint()
    const ip = antiSpamManager.getClientIP()
    antiSpamManager.recordSend(userId, ip)

    // 跟踪反馈成功事件
    analytics.trackFeedbackSuccess(formData.value.rating)

    // 成功提示
    submitResult.value = {
      type: 'success',
      message: t('feedback.submitSuccess')
    }

    // 延迟关闭表单
    setTimeout(() => {
      closeFeedback()
    }, 2000)

  } catch (error) {
    console.error('EmailJS error:', error)
    submitResult.value = {
      type: 'error',
      message: t('feedback.submitError')
    }
    // 跟踪反馈错误事件
    analytics.trackFeedbackError(error instanceof Error ? error.message : 'unknown_error')
  } finally {
    isSubmitting.value = false
  }
}
</script>

<style scoped>
.user-feedback {
  position: fixed;
  bottom: 20px;
  left: 20px;
  z-index: 1000;
}

/* 悬浮按钮 */
.feedback-trigger {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  color: white;
  font-size: 24px;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.feedback-trigger:hover {
  transform: scale(1.1);
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.6);
}

.feedback-trigger:active {
  transform: scale(0.95);
}

/* 弹窗遮罩 */
.feedback-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1001;
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

/* 弹窗内容 */
.feedback-content {
  background: white;
  border-radius: 12px;
  padding: 24px;
  max-width: 500px;
  width: 90%;
  /* max-height: 80vh; */
  overflow-y: auto;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
  animation: slideUp 0.3s ease;
}

@keyframes slideUp {
  from { 
    opacity: 0;
    transform: translateY(30px);
  }
  to { 
    opacity: 1;
    transform: translateY(0);
  }
}

/* 弹窗头部 */
.feedback-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 12px;
  border-bottom: 1px solid #e9ecef;
}

.feedback-header h3 {
  margin: 0;
  color: #333;
  font-size: 1.25rem;
}

.close-btn {
  background: none;
  border: none;
  font-size: 24px;
  color: #666;
  cursor: pointer;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s ease;
}

.close-btn:hover {
  background: #f8f9fa;
  color: #333;
}

/* 表单样式 */
.feedback-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-group label {
  font-weight: 500;
  color: #333;
  font-size: 0.9rem;
}

.form-input,
.form-textarea {
  padding: 12px;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  font-size: 14px;
  transition: border-color 0.2s ease;
  font-family: inherit;
}

.form-input:focus,
.form-textarea:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.form-textarea {
  resize: vertical;
  min-height: 80px;
}

/* 评分样式 */
.rating-container {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.stars {
  display: flex;
  gap: 4px;
}

.star-btn {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  padding: 4px;
  transition: all 0.2s ease;
  filter: grayscale(1);
}

.star-btn.active {
  filter: grayscale(0);
  transform: scale(1.1);
}

.star-btn:hover {
  transform: scale(1.2);
}

.rating-text {
  font-size: 0.9rem;
  color: #666;
  font-weight: 500;
}

/* 按钮样式 */
.form-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 8px;
}

.btn-cancel,
.btn-submit {
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
}

.btn-cancel {
  background: #f8f9fa;
  color: #666;
  border: 1px solid #e9ecef;
}

.btn-cancel:hover {
  background: #e9ecef;
}

.btn-submit {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.btn-submit:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

.btn-submit:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

/* 提交结果 */
.submit-result {
  margin-top: 16px;
  padding: 12px;
  border-radius: 8px;
  font-size: 14px;
}

.submit-result.success {
  background: #d4edda;
  color: #155724;
  border: 1px solid #c3e6cb;
}

.submit-result.error {
  background: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .user-feedback {
    bottom: 15px;
    left: 15px;
  }
  
  .feedback-trigger {
    width: 50px;
    height: 50px;
    font-size: 20px;
  }
  
  .feedback-content {
    margin: 20px;
    padding: 20px;
  }
  
  .form-actions {
    flex-direction: column;
  }
  
  .btn-cancel,
  .btn-submit {
    width: 100%;
  }
}
</style>
